/**
 * ChecklistItem Component
 *
 * Individual checklist item with checkbox, status indicator, and notes capability.
 * Used in audit checklists, pre-audit reviews, and document verification.
 *
 * Features:
 * - Status-based styling (pending, completed, not-applicable, failed)
 * - Optional notes/comments
 * - MHMS clause reference display
 * - Required field indicator
 * - Completion timestamp
 * - Interactive checkbox with keyboard support
 *
 * Status Types:
 * - pending: Default state, not yet checked
 * - completed: Item successfully completed
 * - not-applicable: Item doesn't apply to this case
 * - failed: Item failed verification/audit
 */

import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Check, Circle, Minus, X, ChevronDown, ChevronRight } from 'lucide-react';
import type { ChecklistItemStatus } from '../../hooks/useChecklistState';

export interface ChecklistItemProps {
  id: string;
  label: string;
  description?: string;
  status: ChecklistItemStatus;
  notes?: string;
  reference?: string; // MHMS clause reference (e.g., "MHMS 2020 - 4.2.1")
  required?: boolean;
  completedAt?: string; // ISO date string
  completedBy?: string; // User name or ID
  disabled?: boolean;
  onStatusChange?: (id: string, status: ChecklistItemStatus) => void;
  onNotesChange?: (id: string, notes: string) => void;
  showNotes?: boolean; // Whether to show notes section
  compact?: boolean; // Compact display mode
}

export const ChecklistItem: React.FC<ChecklistItemProps> = ({
  id,
  label,
  description,
  status = 'pending',
  notes = '',
  reference,
  required = false,
  completedAt,
  completedBy,
  disabled = false,
  onStatusChange,
  onNotesChange,
  showNotes = false,
  compact = false,
}) => {
  const { t } = useTranslation(['common', 'auditor']);
  const [isNotesExpanded, setIsNotesExpanded] = useState(showNotes || !!notes);

  const statusConfig = {
    pending: {
      color: 'text-gray-600',
      bgColor: 'bg-gray-100',
      borderColor: 'border-gray-300',
      IconComponent: Circle,
      label: t('common:status.pending'),
    },
    completed: {
      color: 'text-green-600',
      bgColor: 'bg-green-50',
      borderColor: 'border-green-500',
      IconComponent: Check,
      label: t('common:status.completed'),
    },
    'not-applicable': {
      color: 'text-blue-600',
      bgColor: 'bg-blue-50',
      borderColor: 'border-blue-300',
      IconComponent: Minus,
      label: t('common:status.notApplicable'),
    },
    failed: {
      color: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-500',
      IconComponent: X,
      label: t('common:status.failed'),
    },
  };

  const currentStatus = statusConfig[status];

  const handleCheckboxChange = () => {
    if (disabled || !onStatusChange) return;

    // Toggle between completed and pending
    const newStatus: ChecklistItemStatus = status === 'completed' ? 'pending' : 'completed';
    onStatusChange(id, newStatus);
  };

  const handleStatusButtonClick = (newStatus: ChecklistItemStatus) => {
    if (disabled || !onStatusChange) return;
    onStatusChange(id, newStatus);
  };

  const handleNotesChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (disabled || !onNotesChange) return;
    onNotesChange(id, e.target.value);
  };

  const formatDate = (isoDate: string) => {
    const date = new Date(isoDate);
    return date.toLocaleString();
  };

  return (
    <div
      className={`
        border rounded-lg p-4 transition-all
        ${currentStatus.borderColor} ${currentStatus.bgColor}
        ${disabled ? 'opacity-60 cursor-not-allowed' : ''}
        ${compact ? 'p-3' : ''}
      `}
    >
      {/* Main Content */}
      <div className="flex items-start gap-3">
        {/* Checkbox */}
        <div className="flex-shrink-0 pt-1">
          <button
            type="button"
            onClick={handleCheckboxChange}
            disabled={disabled}
            className={`
              w-6 h-6 rounded border-2 flex items-center justify-center
              transition-all focus:outline-none focus:ring-2 focus:ring-offset-2
              ${currentStatus.borderColor} ${currentStatus.bgColor}
              ${disabled ? 'cursor-not-allowed' : 'cursor-pointer hover:scale-110'}
              ${status === 'completed' ? 'focus:ring-green-500' : 'focus:ring-gray-400'}
            `}
            aria-label={`${label} - ${currentStatus.label}`}
            aria-checked={status === 'completed'}
            role="checkbox"
          >
            <currentStatus.IconComponent className={`w-4 h-4 ${currentStatus.color}`} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Label and Required Indicator */}
          <div className="flex items-start justify-between gap-2 mb-1">
            <div className="flex-1">
              <label
                htmlFor={`checklist-item-${id}`}
                className={`
                  font-medium ${currentStatus.color}
                  ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}
                `}
              >
                {label}
                {required && (
                  <span className="text-red-500 ml-1" title={t('common:validation.required')}>
                    *
                  </span>
                )}
              </label>

              {/* Reference (MHMS Clause) */}
              {reference && (
                <div className="text-xs text-gray-500 mt-1">
                  <span className="font-semibold">{t('auditor:auditChecklist.reference')}:</span> {reference}
                </div>
              )}
            </div>

            {/* Status Badge (in compact mode or when not pending) */}
            {(compact || status !== 'pending') && (
              <span
                className={`
                  px-2 py-1 text-xs font-medium rounded
                  ${currentStatus.color} ${currentStatus.bgColor}
                  border ${currentStatus.borderColor}
                `}
              >
                {currentStatus.label}
              </span>
            )}
          </div>

          {/* Description */}
          {description && !compact && (
            <p className="text-sm text-gray-600 mt-1 mb-2">{description}</p>
          )}

          {/* Status Actions (Full Mode) */}
          {!compact && !disabled && onStatusChange && (
            <div className="flex flex-wrap gap-2 mt-3">
              <button
                type="button"
                onClick={() => handleStatusButtonClick('completed')}
                className={`
                  px-3 py-1 text-xs rounded border transition-colors flex items-center gap-1
                  ${status === 'completed' ? 'bg-green-100 border-green-500 text-green-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}
                `}
              >
                <Check className="w-3 h-3" />
                {t('common:status.completed')}
              </button>
              <button
                type="button"
                onClick={() => handleStatusButtonClick('not-applicable')}
                className={`
                  px-3 py-1 text-xs rounded border transition-colors flex items-center gap-1
                  ${status === 'not-applicable' ? 'bg-blue-100 border-blue-500 text-blue-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}
                `}
              >
                <Minus className="w-3 h-3" />
                {t('common:status.notApplicable')}
              </button>
              <button
                type="button"
                onClick={() => handleStatusButtonClick('failed')}
                className={`
                  px-3 py-1 text-xs rounded border transition-colors flex items-center gap-1
                  ${status === 'failed' ? 'bg-red-100 border-red-500 text-red-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}
                `}
              >
                <X className="w-3 h-3" />
                {t('common:status.failed')}
              </button>
              <button
                type="button"
                onClick={() => handleStatusButtonClick('pending')}
                className={`
                  px-3 py-1 text-xs rounded border transition-colors flex items-center gap-1
                  ${status === 'pending' ? 'bg-gray-100 border-gray-500 text-gray-700' : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'}
                `}
              >
                <Circle className="w-3 h-3" />
                {t('common:status.pending')}
              </button>
            </div>
          )}

          {/* Completion Info */}
          {completedAt && status === 'completed' && !compact && (
            <div className="text-xs text-gray-500 mt-2">
              {t('common:status.completedAt')}: {formatDate(completedAt)}
              {completedBy && ` ${t('common:actions.by')} ${completedBy}`}
            </div>
          )}

          {/* Notes Section */}
          {!compact && (
            <div className="mt-3">
              <button
                type="button"
                onClick={() => setIsNotesExpanded(!isNotesExpanded)}
                className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                disabled={disabled}
              >
                {isNotesExpanded ? (
                  <ChevronDown className="w-3 h-3" />
                ) : (
                  <ChevronRight className="w-3 h-3" />
                )}
                <span>{t('auditor:auditChecklist.notes')}</span>
                {notes && <span className="text-xs text-gray-500">({notes.length} {t('common:actions.characters')})</span>}
              </button>

              {isNotesExpanded && (
                <textarea
                  id={`checklist-item-${id}`}
                  value={notes}
                  onChange={handleNotesChange}
                  disabled={disabled}
                  placeholder={t('auditor:auditChecklist.addNotes')}
                  className={`
                    mt-2 w-full px-3 py-2 text-sm border rounded-md
                    focus:outline-none focus:ring-2 focus:ring-blue-500
                    ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
                  `}
                  rows={3}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChecklistItem;
